// Client hook for Cloudflare Turnstile on public forms. It only manages
// widget lifecycle/token state; acceptance is decided server-side by
// submit-lead (siteverify checks the token, `action` and hostname).
import { useCallback, useEffect, useRef, useState } from "react";

const TURNSTILE_SITE_KEY = "0x4AAAAAAFAMP-Q9U7AROA1G";
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
/** Must match TURNSTILE_ACTION in supabase/functions/submit-lead. */
export const TURNSTILE_ACTION = "submit_lead";

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  "refresh-expired"?: "auto" | "manual" | "never";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<void> | null = null;

const loadTurnstileScript = async () => {
  if (typeof window === "undefined") return;
  if (window.turnstile) return;
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile_script_load_failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let the next mount try again instead of caching the failure forever.
      turnstileScriptPromise = null;
      script.remove();
      reject(new Error("turnstile_script_load_failed"));
    };
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

export function useTurnstile() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  /** Script failed to load or the widget errored (blocked by extension, offline…). */
  const [failed, setFailed] = useState(false);

  const reset = useCallback(() => {
    if (typeof window === "undefined" || !window.turnstile || !widgetIdRef.current) return;
    window.turnstile.reset(widgetIdRef.current);
    setToken(null);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: TURNSTILE_ACTION,
          "refresh-expired": "auto",
          callback: (value) => {
            if (cancelled) return;
            setToken(value);
            setFailed(false);
          },
          "error-callback": () => {
            if (cancelled) return;
            setToken(null);
            setFailed(true);
          },
          "expired-callback": () => {
            if (!cancelled) setToken(null);
          },
        });
        if (!cancelled) setIsReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setIsReady(false);
          setToken(null);
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
      setIsReady(false);
      setToken(null);
      if (!window.turnstile || !widgetIdRef.current) return;
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, []);

  return { containerRef, token, isReady, failed, reset };
}
